import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useReconnectingSocket } from '@/shared/hooks/useReconnectingSocket';
import { getWsOrigin, env } from '@/shared/lib/env';
import { getAccessToken } from '@/shared/lib/auth';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiSupportConversationGet } from '@/entities/support-chat/modules/api/supportChat';
import type {
  ApiSupportConversationDetail,
  ApiSupportMessage,
} from '@/shared/types/api/support-chat';

/** Per-conversation frame pushed by AdminChatConsumer.chat_message. */
type ChatMessageEvent = {
  type: 'message';
  message: ApiSupportMessage;
};

const isChatMessageEvent = (data: unknown): data is ChatMessageEvent =>
  typeof data === 'object' &&
  data !== null &&
  (data as { type?: unknown }).type === 'message' &&
  typeof (data as { message?: unknown }).message === 'object';

/**
 * Opens `wss://<api>/ws/admin/chat/<userId>?token=<jwt>` while a conversation is
 * open. Incoming `chat.message` frames are appended (deduped by id) to the
 * conversation detail query cache. On (re)connect we refetch once to backfill
 * anything missed while the socket was down.
 *
 * `onAppend` lets the thread view mirror new messages into its local state.
 */
export const useAdminChatSocket = (
  userId: string,
  onAppend?: (message: ApiSupportMessage) => void,
): void => {
  const qc = useQueryClient();
  const token = getAccessToken();

  const url = useMemo(() => {
    const origin = getWsOrigin(env.apiUrl);
    if (!userId || !token || !origin) return null;
    return `${origin}/ws/admin/chat/${userId}?token=${encodeURIComponent(token)}`;
  }, [userId, token]);

  const onMessage = useCallback(
    (data: unknown) => {
      if (!isChatMessageEvent(data)) return;
      const message = data.message;
      if (message.conversation_id == null) return;

      qc.setQueryData<ApiSupportConversationDetail>(
        queryKeys.supportChat.conversation(userId),
        (prev) => {
          if (!prev) return prev;
          if (prev.messages.some((m) => m.id === message.id)) return prev;
          return { ...prev, messages: [...prev.messages, message] };
        },
      );
      onAppend?.(message);
      // The socket handler already merged the new message into the open thread
      // cache above, so only refresh the conversations list + unread badge —
      // invalidating `.all` would force a redundant refetch of this thread.
      void qc.invalidateQueries({ queryKey: queryKeys.supportChat.conversationsList });
      void qc.invalidateQueries({ queryKey: queryKeys.supportChat.unreadCount });
    },
    [qc, userId, onAppend],
  );

  const onOpen = useCallback(() => {
    if (!userId) return;
    void apiSupportConversationGet(userId)
      .then((detail) =>
        qc.setQueryData(queryKeys.supportChat.conversation(userId), detail),
      )
      .catch(() => {
        // transient; backoff reconnect will retry the sync
      });
  }, [qc, userId]);

  useReconnectingSocket({ url, onMessage, onOpen });
};
